import React from "react"
import {useTranslation} from "react-i18next"
import {ArrowTrendingDownIcon} from "@heroicons/react/20/solid"
import ArrowTrendingUpIcon from "@heroicons/react/20/solid/ArrowTrendingUpIcon"
import {InformationCircleIcon} from "@heroicons/react/24/outline"
import {twMerge} from "tailwind-merge"

import {IconButton} from "../../components/Button.tsx"
import {Tooltip} from "../../components/Tooltip.tsx"
import {ASalesIntelligenceSalesCycleIterationStatistics} from "../../services/types.generated.ts"

export const SalesCycleStatistics: React.FC<{
  salesCycleStatistics: ASalesIntelligenceSalesCycleIterationStatistics
}> = ({salesCycleStatistics}) => {
  const {t} = useTranslation()

  return (
    <div
      className={
        "flex flex-wrap items-center justify-start gap-5 self-stretch rounded-tl-lg rounded-tr-lg rounded-b-none border-b-0 bg-white px-2"
      }
    >
      <div
        className={"inline-flex w-full flex-col items-start justify-start gap-2 rounded-lg p-7 md:w-[45%] lg:w-auto"}
      >
        <div className={"inline-flex items-center justify-center gap-1"}>
          <div className={"text-xm justify-start leading-tight font-light"}>
            {t("Dashboard_SaaS_SalesCycleStatistics_ActiveDeals")}
          </div>
          <Tooltip
            buttonNode={
              <IconButton noEdges>
                <InformationCircleIcon className={"h-5 w-5 flex-shrink-0 text-cr-grey-50"} />
              </IconButton>
            }
            openDelay={200}
          >
            <div className={"rounded-lg bg-gray-900/90 px-2 py-2 text-sm text-cr-white"}>
              {t("Dashboard_SaaS_SalesCycleStatistics_ActiveDealsTooltip")}
            </div>
          </Tooltip>
        </div>
        <div className={"justify-start"}>
          <span className={"font-['Sora'] text-3xl leading-snug font-bold text-gray-900"}>
            {salesCycleStatistics.active_deals ?? "--"}
          </span>
          <span className={"font-['Sora'] text-3xl leading-snug font-bold text-zinc-500"}>
            /{salesCycleStatistics.deals ?? "--"}
          </span>
        </div>
      </div>
      <div className={"mx-2 hidden border-r py-10 md:block"}></div>
      <div
        className={"inline-flex w-full flex-col items-start justify-start gap-2 rounded-lg p-7 md:w-[45%] lg:w-auto"}
      >
        <div className={"inline-flex items-center justify-center gap-1"}>
          <div className={"text-xm justify-start leading-tight font-light"}>
            {t("Dashboard_SaaS_SalesCycleStatistics_ActiveDealsToCalls")}
          </div>
          <Tooltip
            buttonNode={
              <IconButton noEdges>
                <InformationCircleIcon className={"h-5 w-5 flex-shrink-0 text-cr-grey-50"} />
              </IconButton>
            }
            openDelay={200}
          >
            <div className={"rounded-lg bg-gray-900/90 px-2 py-2 text-sm text-cr-white"}>
              {t("Dashboard_SaaS_SalesCycleStatistics_ActiveDealsToCallsTooltip")}
            </div>
          </Tooltip>
        </div>
        <div className={"inline-flex items-start justify-start gap-2"}>
          <div className={"justify-start font-['Sora'] text-3xl leading-snug font-bold text-gray-900"}>
            {salesCycleStatistics.deals_to_calls_ratio ?? "--"}%
          </div>
          <PercentageChange value={salesCycleStatistics.deals_to_calls_change ?? null} />
        </div>
      </div>
      <div className={"mx-2 hidden border-r py-10 lg:block"}></div>
      <div
        className={"inline-flex w-full flex-col items-start justify-start gap-2 rounded-lg p-7 md:w-[45%] lg:w-auto"}
      >
        <div className={"inline-flex items-center justify-center gap-1"}>
          <div className={"text-xm justify-start leading-tight font-light"}>
            {t("Dashboard_SaaS_SalesCycleStatistics_ActiveDealsToMeetings")}
          </div>
          <Tooltip
            buttonNode={
              <IconButton noEdges>
                <InformationCircleIcon className={"h-5 w-5 flex-shrink-0 text-cr-grey-50"} />
              </IconButton>
            }
            openDelay={200}
          >
            <div className={"rounded-lg bg-gray-900/90 px-2 py-2 text-sm text-cr-white"}>
              {t("Dashboard_SaaS_SalesCycleStatistics_ActiveDealsToMeetingsTooltip")}
            </div>
          </Tooltip>
        </div>
        <div className={"inline-flex items-start justify-start gap-2"}>
          <div className={"justify-start font-['Sora'] text-3xl leading-snug font-bold text-gray-900"}>
            {salesCycleStatistics.deals_to_meetings_ratio ?? "--"}%
          </div>
          <PercentageChange value={salesCycleStatistics.deals_to_meetings_change ?? null} />
        </div>
      </div>
      <div className={"mx-2 hidden border-r py-10 md:block"}></div>
      <div
        className={"inline-flex w-full flex-col items-start justify-start gap-2 rounded-lg p-7 md:w-[45%] lg:w-auto"}
      >
        <div className={"inline-flex items-center justify-center gap-1"}>
          <div className={"text-xm justify-start leading-tight font-light"}>
            {t("Dashboard_SaaS_SalesCycleStatistics_RottenDeals")}
          </div>
          <Tooltip
            buttonNode={
              <IconButton noEdges>
                <InformationCircleIcon className={"h-5 w-5 flex-shrink-0 text-cr-grey-50"} />
              </IconButton>
            }
            openDelay={200}
          >
            <div className={"rounded-lg bg-gray-900/90 px-2 py-2 text-sm text-cr-white"}>
              {t("Dashboard_SaaS_SalesCycleStatistics_RottenDealsTooltip")}
            </div>
          </Tooltip>
        </div>
        <div className={"inline-flex items-start justify-start gap-2"}>
          <div className={"justify-start font-['Sora'] text-3xl leading-snug font-bold text-gray-900"}>
            {salesCycleStatistics.rotten_deals ?? "--"}
          </div>
          <PercentageChange value={salesCycleStatistics.rotten_deals_change ?? null} invertPositive />
        </div>
      </div>
      <div className={"mx-2 hidden border-r py-10 lg:block"}></div>
      <div
        className={"inline-flex w-full flex-col items-start justify-start gap-2 rounded-lg p-7 md:w-[50%] lg:w-auto"}
      >
        <div className={"inline-flex items-center justify-center gap-1"}>
          <div className={"text-xm justify-start leading-tight font-light"}>
            {t("Dashboard_SaaS_SalesCycleStatistics_ActivitiesToWon")}
          </div>
          <Tooltip
            buttonNode={
              <IconButton noEdges>
                <InformationCircleIcon className={"h-5 w-5 flex-shrink-0 text-cr-grey-50"} />
              </IconButton>
            }
            openDelay={200}
          >
            <div className={"rounded-lg bg-gray-900/90 px-2 py-2 text-sm text-cr-white"}>
              {t("Dashboard_SaaS_SalesCycleStatistics_ActivitiesToWonTooltip")}
            </div>
          </Tooltip>
        </div>
        <div className={"inline-flex items-start justify-start gap-2"}>
          <div className={"justify-start font-['Sora'] text-3xl leading-snug font-bold text-gray-900"}>
            {salesCycleStatistics.activities_to_won_average ?? "--"}
          </div>
        </div>
      </div>
    </div>
  )
}

export const PercentageChange: React.FC<{
  value: number | null
  invertPositive?: boolean
}> = ({value, invertPositive = false}) => {
  const hasNoChange = value == null

  // Calculate if the change is positive based on the value and whether to invert the logic
  const isPositive = hasNoChange ? false : invertPositive ? value <= 0 : value >= 0

  return (
    <div className={"flex items-center justify-center gap-1 rounded-2xl px-2 py-2.5"}>
      <div
        className={twMerge(
          "flex items-center gap-1 text-sm",
          hasNoChange ? "text-cr-grey-50" : isPositive ? "text-cr-green" : "text-cr-red"
        )}
      >
        {(value ?? 0) >= 0 ? (
          <ArrowTrendingUpIcon className={"h-4 w-4"} />
        ) : (
          <ArrowTrendingDownIcon className={"h-4 w-4"} />
        )}
        <span>{value == null ? "--" : Math.abs(value)}%</span>
      </div>
    </div>
  )
}
